// The staff intake road: conflict check and matter creation against
// /api/intake, template list and the classifier against
// /api/practice-areas. The server's sentence is the actionable part of
// every failure - surface body.error, fall back to the status.

import type {
  IntakeSubmissionView,
  IntakeTakeIntoWorkResponse,
  IntakeTemplate,
  IntakeTemplateResetResponse,
  IntakeTemplateResponse,
  IntakeTranslateRequest,
  IntakeTranslateResponse,
  IntakeTranslationClaim,
} from "@duralex/shared";
import { downloadStaffFile, getAuthHeaders } from "../../../api/auth";
import type { PracticeAreaTemplate } from "../../../hooks/usePracticeAreaTemplate";

/** The status rides along: a 429 pauses the composer, a 409 or 404 on an
    interview points at the list instead of a retry. */
export class IntakeApiError extends Error {
  status: number;
  constructor(message: string, status: number) {
    super(message);
    this.status = status;
  }
}

async function intakeJson<T>(res: Response, fallback: string): Promise<T> {
  if (!res.ok) {
    const body = (await res.json().catch(() => null)) as { error?: string } | null;
    throw new IntakeApiError(body?.error ?? `${fallback} (HTTP ${res.status})`, res.status);
  }
  return (await res.json()) as T;
}

function post(path: string, payload: unknown): Promise<Response> {
  return fetch(path, {
    method: "POST",
    headers: { "Content-Type": "application/json", ...getAuthHeaders() },
    credentials: "same-origin",
    body: JSON.stringify(payload),
  });
}

// ---------------------------------------------------------------------------
// Conflicts
// ---------------------------------------------------------------------------

export interface ConflictCandidate {
  type: "duplicate-client" | "opposing-party" | "prior-adverse" | "name-similarity";
  severity: "info" | "warning" | "critical";
  message: string;
  existingClient?: {
    _id: string;
    firstName: string;
    lastName: string;
    alienNumber: string | null;
    dateOfBirth: string | null;
    email: string | null;
    primaryPhone: string | null;
  };
  existingMatter?: {
    _id: string;
    caseNumber: string | null;
    practiceArea: string | null;
    caseType: string | null;
    status: string | null;
  };
  details?: string;
}

export interface ConflictCheckInput {
  firstName: string;
  lastName: string;
  alienNumber?: string;
  dateOfBirth?: string;
  email?: string;
  primaryPhone?: string;
  opposingParties?: string[];
}

export async function checkConflicts(input: ConflictCheckInput): Promise<ConflictCandidate[]> {
  const res = await post("/api/intake/conflict-check", input);
  const body = await intakeJson<{ candidates?: ConflictCandidate[] }>(
    res,
    "The conflict check failed",
  );
  return body.candidates ?? [];
}

// ---------------------------------------------------------------------------
// Practice-area templates + classifier
// ---------------------------------------------------------------------------

export type TemplateListItem = Pick<PracticeAreaTemplate, "id" | "name" | "parent" | "icon">;

export async function listIntakeTemplates(): Promise<TemplateListItem[]> {
  const res = await fetch("/api/practice-areas", {
    headers: { ...getAuthHeaders() },
    credentials: "same-origin",
  });
  const body = await intakeJson<{ templates?: TemplateListItem[] }>(
    res,
    "Could not load the practice-area list",
  );
  return body.templates ?? [];
}

export interface ClassificationResult {
  templateId?: string;
  confidence?: number;
  alternatives?: Array<{ templateId?: string; confidence?: number }>;
  reasoning?: string;
}

export async function classifyDescription(description: string): Promise<ClassificationResult> {
  const res = await post("/api/practice-areas/classify", { description });
  return intakeJson<ClassificationResult>(res, "The classifier is unavailable");
}

// ---------------------------------------------------------------------------
// Wizard create
// ---------------------------------------------------------------------------

export interface CreateIntakeInput {
  client: {
    firstName: string;
    lastName: string;
    alienNumber?: string;
    dateOfBirth?: string;
    email?: string;
    primaryPhone?: string;
    preferredLanguage?: string;
  };
  description: string;
  practiceAreaTemplateId?: string | null;
  opposingParties?: string[];
  acknowledgedConflicts?: boolean;
}

export interface CreatedMatterShape {
  _id: string;
  eId?: string;
  caseNumber?: string;
  status?: string;
  needsReview?: boolean;
}

export interface CreateIntakeResponse {
  client: {
    _id: string;
    firstName: string;
    lastName: string;
    alienNumber: string | null;
    email: string | null;
    primaryPhone: string | null;
    wasCreated: boolean;
    matchType: "alienNumber" | "phone" | "email" | "name+dob" | "new";
  } | null;
  matter: CreatedMatterShape;
  practiceAreaTemplateId: string | null;
  // null when the caller supplied a template id; on a classifier failure
  // only `reasoning` is present - a 201 is not "the area was picked".
  classification: ClassificationResult | null;
  conflicts: ConflictCandidate[];
}

export async function createIntakeMatter(input: CreateIntakeInput): Promise<CreateIntakeResponse> {
  const res = await post("/api/intake/create", input);
  return intakeJson<CreateIntakeResponse>(res, "The matter could not be created");
}

// ---------------------------------------------------------------------------
// AI interview
// ---------------------------------------------------------------------------

// Mirrors MAX_INTERVIEW_TURNS in the server's modules/intake/routes/intake.ts: two history entries per
// exchange, and the server refuses the next one with a 409 at the cap.
export const INTERVIEW_MAX_TURNS = 40;

export interface InterviewTurn {
  role: "user" | "assistant";
  content: string;
}

export interface InterviewState {
  sessionId: string;
  caseType: string | null;
  practiceArea: string;
  keyFacts: string[];
  suggestedQuestions: string[];
  extractedData: Record<string, unknown>;
}

export interface InterviewSession extends InterviewState {
  clientName: string | null;
  history: InterviewTurn[];
  startedByEmail: string | null;
  createdAt: string;
  updatedAt: string;
}

export interface StartInterviewInput {
  clientName?: string;
  content: string;
}

export async function startInterview(input: StartInterviewInput): Promise<InterviewState> {
  const res = await post("/api/intake/start", { type: "text", ...input });
  return intakeJson<InterviewState>(res, "The interview could not start");
}

export async function fetchInterview(sessionId: string): Promise<InterviewSession> {
  const res = await fetch(`/api/intake/${encodeURIComponent(sessionId)}`, {
    headers: { ...getAuthHeaders() },
    credentials: "same-origin",
  });
  return intakeJson<InterviewSession>(res, "The interview could not be loaded");
}

/** A questionnaire as the office reads it, rendered by the server. */
export async function fetchSubmission(sessionId: string): Promise<IntakeSubmissionView> {
  const res = await fetch(`/api/intake/${encodeURIComponent(sessionId)}/answers`, {
    headers: { ...getAuthHeaders() },
    credentials: "same-origin",
  });
  return intakeJson<IntakeSubmissionView>(res, "The answers could not be loaded");
}

export interface AnswerInterviewInput {
  questionId: number;
  answer: string;
}

export interface AnswerInterviewResult extends InterviewState {
  readyToCreate: boolean;
}

/** The same view on the firm's paper; the server names the file like the Drive record. */
export function downloadIntakePdf(sessionId: string, fileName: string): Promise<void> {
  return downloadStaffFile(`/api/intake/${encodeURIComponent(sessionId)}/answers.pdf`, fileName);
}

export async function answerInterview(
  sessionId: string,
  input: AnswerInterviewInput,
): Promise<AnswerInterviewResult> {
  const res = await post(`/api/intake/${encodeURIComponent(sessionId)}/answer`, input);
  return intakeJson<AnswerInterviewResult>(res, "The answer was not taken");
}

export interface InterviewUploadResult {
  document: { _id: string; fileName?: string; status?: string } | null;
  // false: the file is kept and staged, but nothing was read from it and
  // the transcript does not mention it.
  read: boolean;
  extractedData: Record<string, unknown>;
}

export async function uploadInterviewDocument(
  sessionId: string,
  file: File,
): Promise<InterviewUploadResult> {
  const form = new FormData();
  form.append("file", file);
  const res = await fetch(`/api/intake/${encodeURIComponent(sessionId)}/documents`, {
    method: "POST",
    headers: { ...getAuthHeaders() },
    credentials: "same-origin",
    body: form,
  });
  return intakeJson<InterviewUploadResult>(res, "The upload failed");
}

export interface CreateInterviewResponse {
  client: {
    _id: string;
    firstName: string;
    lastName: string;
    wasCreated: boolean;
    matchType: string;
  } | null;
  matter: CreatedMatterShape;
  conflicts: ConflictCandidate[];
  documents: { assigned: number; moved: number; total: number };
}

export async function createInterviewMatter(sessionId: string): Promise<CreateInterviewResponse> {
  const res = await post(`/api/intake/${encodeURIComponent(sessionId)}/create-case`, {});
  return intakeJson<CreateInterviewResponse>(res, "The matter could not be created");
}

export type IntakeSessionSummary = Omit<
  InterviewSession,
  "history" | "suggestedQuestions" | "extractedData"
> & {
  kind: "interview" | "questionnaire";
  status: "invited" | "submitted" | null;
  category: string | null;
  /** The category's label while the questionnaire still has it. */
  categoryLabel: string | null;
  language: string | null;
  email: string | null;
  openedAt: string | null;
  expiresAt: string | null;
  /** The claimed matter's eId, when the questionnaire became one. */
  matterEid: string | null;
  /** Who minted a questionnaire: a staff link or the public page; an
      interview has no source. */
  source: "link" | "public" | null;
  /** How far the client got: answers to the questions their category
      asks, and how many those are. An interview has neither. */
  answered: number | null;
  questions: number | null;
  submittedAt: string | null;
  /** The last reminder a public row got, and from whom. */
  remindedAt: string | null;
  remindedBy: string | null;
};

// Mirrors SESSION_LIST_LIMIT in the server's modules/intake/routes/intake.ts, a page per card. The
// interviews page is the latest 50; the questionnaires page is 50 filled
// open-first, then waiting, then claimed, so what a full page leaves out
// is its lowest-priority rows, not its oldest.
export const INTAKE_LIST_CAP = 50;

export async function listIntakeSessions(): Promise<IntakeSessionSummary[]> {
  const res = await fetch("/api/intake", {
    headers: { ...getAuthHeaders() },
    credentials: "same-origin",
  });
  const body = await intakeJson<{ sessions?: IntakeSessionSummary[] }>(
    res,
    "The interview list could not be read",
  );
  return body.sessions ?? [];
}

/** Interviews and questionnaire links go through the same door; for a
    link this is "revoke". A submitted questionnaire is refused. */
export async function deleteIntakeSession(sessionId: string): Promise<void> {
  const res = await fetch(`/api/intake/${encodeURIComponent(sessionId)}`, {
    method: "DELETE",
    headers: { ...getAuthHeaders() },
    credentials: "same-origin",
  });
  await intakeJson<{ ok: boolean }>(res, "The session could not be deleted");
}

// ---------------------------------------------------------------------------
// Questionnaire links
// ---------------------------------------------------------------------------

export interface IntakeLinkIssue {
  sessionId: string;
  url: string;
  expiresAt: string;
  /** Present only when an address was given. */
  email?: { to: string; delivered: "sent" | "queued" | "not_delivered" };
}

export interface CreateIntakeLinkInput {
  clientName?: string;
  category?: string;
  language?: string;
  email?: string;
}

export async function createIntakeLink(input: CreateIntakeLinkInput): Promise<IntakeLinkIssue> {
  const res = await post("/api/intake/links", input);
  return intakeJson<IntakeLinkIssue>(res, "The link could not be created");
}

export async function resendIntakeLink(sessionId: string): Promise<IntakeLinkIssue> {
  const res = await post(`/api/intake/links/${encodeURIComponent(sessionId)}/resend`, {});
  return intakeJson<IntakeLinkIssue>(res, "The link could not be renewed");
}

export interface IntakeReminderIssue {
  sessionId: string;
  expiresAt: string;
  remindedAt: string;
  remindedBy: string | null;
  email: { to: string; delivered: "sent" | "queued" };
}

/** A public questionnaire's client is asked back by mail; its week
    starts again. The server refuses, with its reason, when the letter
    cannot go. */
export async function remindIntake(sessionId: string): Promise<IntakeReminderIssue> {
  const res = await post(`/api/intake/${encodeURIComponent(sessionId)}/remind`, {});
  return intakeJson<IntakeReminderIssue>(res, "The reminder could not be sent");
}

/** A submitted questionnaire with no matter yet becomes one, owned by the
    caller. The server's 409s differ: a conflict refusal comes before any
    mint and may be retried with the acknowledgement; the rest may not. */
export async function takeIntoWork(
  sessionId: string,
  acknowledgedConflicts?: boolean,
): Promise<IntakeTakeIntoWorkResponse> {
  const res = await post(
    `/api/intake/${encodeURIComponent(sessionId)}/take-into-work`,
    acknowledgedConflicts ? { acknowledgedConflicts: true } : {},
  );
  return intakeJson<IntakeTakeIntoWorkResponse>(
    res,
    "The questionnaire could not be taken into work",
  );
}

export async function fetchIntakeTemplate(): Promise<IntakeTemplateResponse> {
  const res = await fetch("/api/intake/template", {
    headers: { ...getAuthHeaders() },
    credentials: "same-origin",
  });
  return intakeJson<IntakeTemplateResponse>(res, "The questionnaire template could not be read");
}

/** PUT body: the template minus the server-owned version, plus the version
    it was loaded from - a stale one is refused with 409, never merged - and
    the cells a model wrote, so the record says so. */
export type IntakeTemplatePut = Omit<IntakeTemplate, "version"> & {
  expectedVersion?: number;
  aiTranslations?: IntakeTranslationClaim[];
};

export async function saveIntakeTemplate(body: IntakeTemplatePut): Promise<IntakeTemplateResponse> {
  const res = await fetch("/api/intake/template", {
    method: "PUT",
    headers: { "Content-Type": "application/json", ...getAuthHeaders() },
    credentials: "same-origin",
    body: JSON.stringify(body),
  });
  return intakeJson<IntakeTemplateResponse>(res, "The questionnaire could not be saved");
}

/** One chunk of labels to a model. The server checks the proposal and
    hands back only what it would accept, with the digest a save's claim
    carries; the editor decides what to keep. */
export async function translateIntakeTemplate(
  body: IntakeTranslateRequest,
): Promise<IntakeTranslateResponse> {
  const res = await post("/api/intake/template/translate", body);
  return intakeJson<IntakeTranslateResponse>(res, "The translation could not be made");
}

export async function resetIntakeTemplate(): Promise<IntakeTemplateResetResponse> {
  const res = await fetch("/api/intake/template", {
    method: "DELETE",
    headers: { ...getAuthHeaders() },
    credentials: "same-origin",
  });
  return intakeJson<IntakeTemplateResetResponse>(res, "The questionnaire could not be reset");
}
